<template>
    <div v-if="carregando" class="loading" role="status" aria-label="Carregando pacotes de trabalho">
        <div></div>
    </div>
    <template v-else>
    <div v-if="resultados.length">
        <h3>Entregas</h3>
        <div class="tags">
            <a v-for="(resultado, index) in resultados" :key="resultado.id" href="#" class="resultado"
                :class="{ ativo: resultadoSelecionadoId == resultado.id }"
                :title="tituloResultado(resultado, index)" @click.prevent="selecionarResultado(resultado.id)">
                {{ rotuloResultado(resultado, index) }}
            </a>
        </div>
        <br>
    </div>
    <div>
        <h3>Pacotes de trabalho</h3>
        <div class="entregas-lista-bloco">
            <!--
              O root do draggable precisa ser o próprio grid (evitar display:contents no host).
              Sortable calcula hit-test nos filhos diretos; com contents o layout 2D quebrava o preview.
              Botão «Adicionar» no slot footer (sem data-draggable) — não entra na ordem.
            -->
            <draggable v-model="entregasExibidas" item-key="id" :disabled="!podeReordenarEntregas" tag="div"
                class="cards-entregas-drag-host" :animation="0" :empty-insert-threshold="40"
                chosen-class="card-entrega--sortable-chosen" ghost-class="card-entrega--sortable-ghost"
                :move="podeMoverEntregaNoDrag" @start="onEntregasDragStart" @end="onEntregasDragEnd">
                <template #item="{ element: e }">
                    <div class="card-entrega" :class="{
                        selecionado: entregaSelecionada?.id == e.id,
                        'card-entrega--arrastavel': podeReordenarEntregas,
                        'card-entrega--incompleta': entregaIncompleta(e)
                    }" @click="mudarEntrega(e)">
                        <div class="card-entrega__nome">{{ e.index + 1 }}. {{ e.nome || 'Sem nome' }}</div>
                        <div class="card-entrega__meta">
                            <span class="card-entrega__label">Responsável</span>
                            <strong class="card-entrega__valor">{{ nomeCoordenador(e.coordenador_id) }}</strong>
                        </div>
                        <div class="card-entrega__meta card-entrega__meta--prazo">
                            <span class="card-entrega__label">Prazo</span>
                            <span class="chip" :class="classePrazo(e)">{{ textoPrazo(e) }}</span>
                        </div>
                        <div class="card-entrega__meta card-entrega__meta--prazo">
                            <span class="card-entrega__label">Custo</span>
                            <span class="chip bg-cinza">{{ formatarCustoEntrega(e) }}</span>
                        </div>
                    </div>
                </template>
                <template #footer>
                    <button v-if="podeEditar && (isGerente || isAuditor)" type="button"
                        class="card-entrega card-entrega--add" @click.stop="showModal = true">
                        <i class="bi-plus-circle"></i>
                        <span>Adicionar pacote de trabalho</span>
                    </button>
                </template>
            </draggable>
        </div>

        <fieldset v-if="entregaSelecionada?.id" class="form-entrega">
            <div class="cabecalho-form-entrega">
                <h3>Dados do pacote de trabalho selecionado</h3>
                <span v-if="entregaSelecionadaConcluida" class="chip bg-sucesso">Pacote de trabalho concluído — somente
                    leitura</span>
                <button v-if="podeEditarEntregaSelecionada && (isGerente || isAuditor)" type="button"
                    class="btn-excluir-entrega" @click="excluirEntrega(entregaSelecionada.id)" title="Excluir pacote de trabalho">
                    <i class="bi-trash-fill"></i>
                    Excluir pacote de trabalho
                </button>
            </div>
            <div class="grid-4">
                <div class="col-2">
                    <label>Nome</label>
                    <input type="text" v-model="entregaSelecionada.nome"
                        :readonly="!podeEditarEntregaSelecionada || (!isAuditor && !isGerente)"
                        @change="atualizarEntrega('nome', entregaSelecionada.nome, entregaSelecionada.id)">
                </div>
                <div>
                    <label>Coordenador do pacote de trabalho</label>
                    <select v-model="entregaSelecionada.coordenador_id"
                        :disabled="!podeEditarEntregaSelecionada || (!isAuditor && !isGerente)"
                        @change="atualizarEntrega('coordenador_id', entregaSelecionada.coordenador_id, entregaSelecionada.id)">
                        <option v-for="item, index in coordenadores" :key="index" :value="item.id">
                            {{ item.usuario_nome }}
                        </option>
                    </select>
                </div>
                <div>
                    <label>Entrega a ser alcançada</label>
                    <select v-model="entregaSelecionada.resultado_id" :required="!entregaSelecionada.resultado_id"
                        :disabled="!podeEditarEntregaSelecionada || (!isAuditor && !isGerente)"
                        @change="atualizarEntrega('resultado_id', entregaSelecionada.resultado_id, entregaSelecionada.id)">
                        <option :value="null" hidden>N/A</option>
                        <option v-for="item, index in resultados" :key="item.id" :value="item.id"
                            :title="tituloResultado(item, index)">
                            {{ rotuloResultado(item, index) }}
                        </option>
                    </select>
                </div>
                <div>
                    <label>Início Previsto</label>
                    <input :readonly="!podeEditarEntregaSelecionada || (!isAuditor && !isGerente)" type="date"
                        @change="atualizarEntrega('dtInicio', entregaSelecionada.dtInicio, entregaSelecionada.id)"
                        v-model="entregaSelecionada.dtInicio" :max="entregaSelecionada.dtFim || propPcm.dtFim">
                </div>
                <div>
                    <label>Fim Previsto</label>
                    <div class="fim-previsto">
                        <input
                            :readonly="!podeEditarEntregaSelecionada || (!isAuditor && !isGerente) || temObservacaoRepactuacao"
                            :title="temObservacaoRepactuacao ? 'Não é possível alterar a data após uma repactuação' : ''"
                            type="date" @change="atualizarEntrega('dtFim', entregaSelecionada.dtFim, entregaSelecionada.id)"
                            v-model="entregaSelecionada.dtFim" :min="entregaSelecionada.dtInicio || propPcm.dtInicio"
                            :max="propPcm.dtFim">
                        <span v-if="exibirFimPrevistoOriginal(entregaSelecionada, entregaSelecionada.dtFim)"
                            class="chip bg-erro fim-previsto-original"
                            :title="tituloFimPrevistoOriginal(entregaSelecionada)">
                            Original: {{ formatarDataBr(fimPrevistoOriginal(entregaSelecionada)) }}
                        </span>
                    </div>
                </div>
                <div>
                    <label>Data de conclusão</label>
                    <input v-if="podeEditar && isAuditor" type="date"
                        v-model="entregaSelecionada.dt_conclusao"
                        @change="atualizarEntrega('dt_conclusao', entregaSelecionada.dt_conclusao ? String(entregaSelecionada.dt_conclusao).slice(0, 10) : null, entregaSelecionada.id)"
                        title="Auditores (func. 116) podem ajustar; preenchida automaticamente ao concluir" />
                    <input v-else type="date" readonly :value="dataParaInput(entregaSelecionada.dt_conclusao)"
                        title="Preenchida automaticamente ao concluir o pacote de trabalho">
                </div>
                <div>
                    <label>Status</label>
                    <BarraProgresso :propItem="{ id: entregaSelecionada.id, status: entregaSelecionada.status }"
                        :idUsuario="idUsuario" :isAuditor="podeEditarEntregaSelecionada && (isAuditor || isGerente)"
                        :isCoordenador="podeEditarEntregaSelecionada && isCoordenador(entregaSelecionada.coordenador_id)"
                        @editarStatus="editarStatus" />
                </div>
            </div>
            <div class="grid-3 bloco-form">
                <!-- <div>
                    <label>Tarefas</label>
                    <div class="tarefas grid-2">
                        <span class="chip bg-alerta">Pendentes: <span>{{ entregaSelecionada.pendente }}</span></span>
                        <span class="chip bg-ok">Andamento: <span>{{ entregaSelecionada.em_andamento }}</span></span>
                        <span class="chip bg-sucesso">Concluídas: <span>{{ entregaSelecionada.concluido }}</span></span>
                        <span class="chip bg-erro">Atrasadas: <span>{{ entregaSelecionada.atrasada }}</span></span>
                        <span class="chip bg-cinza col-2">Total: <span>{{ entregaSelecionada.total }}</span></span>
                    </div>
                </div> -->
                <div :class="{ 'col-2': !temObservacaoRepactuacao }">
                    <label>Observação</label>
                    <textarea
                        :readonly="!podeEditarEntregaSelecionada || (!isCoordenador(entregaSelecionada.coordenador_id) && !isAuditor && !isGerente)"
                        v-model="entregaSelecionada.observacao"
                        @change="atualizarEntrega('observacao', entregaSelecionada.observacao, entregaSelecionada.id)"></textarea>
                </div>
                <div v-if="temObservacaoRepactuacao">
                    <label>Repactuação</label>
                    <textarea readonly v-model="entregaSelecionada.observacao_repactuacao"></textarea>
                </div>
                <div v-if="entregaSelecionada.resultado_alcancado">
                    <label>Entrega alcançada</label>
                    <textarea readonly v-model="entregaSelecionada.resultado_alcancado"></textarea>
                </div>
                <div class="col-3">
                    <label>Reuniões em que foi discutido</label>
                    <p v-if="carregandoReunioesPacote" class="fonte-fraca fonte-menor">Carregando reuniões…</p>
                    <p v-else-if="reunioesPacote.length === 0" class="fonte-fraca fonte-menor">
                        Este pacote ainda não foi discutido em nenhuma weekly.
                    </p>
                    <ul v-else class="lista-reunioes-pacote">
                        <li v-for="reuniao in reunioesPacote" :key="reuniao.id">
                            <span class="chip bg-cinza">{{ formatarDataHoraBr(reuniao.start) }}</span>
                            <a v-if="reuniao.html_link" :href="reuniao.html_link" target="_blank" rel="noopener">
                                {{ reuniao.summary || 'Reunião sem título' }}
                            </a>
                            <span v-else>{{ reuniao.summary || 'Reunião sem título' }}</span>
                        </li>
                    </ul>
                </div>
            </div>
        </fieldset>

    </div>
    </template>
    <ModalNovaEntrega v-if="showModal" @fecharModal="showModal = false" :propPcm="propPcm"
        :coordenadores="coordenadores" :resultadoSelecionadoId="resultadoSelecionadoId"
        @salvarEntrega="gravarNovaEntrega" />
    <ModalResultadoEntrega :mostrar="modalResultado" :entrega="entregaEditada" @fechar="fecharModalResultado"
        @salvar="concluirEntrega" />
</template>

<script>
import BarraProgresso from '@/components/BarraProgresso.vue';
import ModalNovaEntrega from '@/components/Modais/projetos/ModalNovaEntrega.vue';
import ModalResultadoEntrega from '@/components/Modais/projetos/ModalResultadoEntrega.vue';
import serviceProjetos from '@/services/serviceProjetos';
import { dataParaInput, formatarDataBr, formatarDataHoraBr } from '@/utils/date';
import { exibirFimPrevistoOriginal, fimPrevistoOriginal, tituloFimPrevistoOriginal } from '@/utils/fim-previsto-historico';
import { rotuloResultado, tituloResultado } from '@/utils/resultado-rotulo';
import {
    entregaEstaConcluida as entregaConcluida,
    entregaStatusEhCancelado,
    entregaStatusEhConcluido,
    extrairTarefasDeSprints,
    mensagemTarefasImpedemConclusaoEntrega,
    tarefasImpedemConclusaoEntrega,
} from '@/utils/tarefaStatus';
import draggable from 'vuedraggable';

export default {
    name: 'EntregasComponent',
    components: {
        ModalNovaEntrega,
        ModalResultadoEntrega,
        BarraProgresso,
        draggable,
    },
    props: {
        propPcm: {
            default: {},
        },
        idProjeto: {
            required: true,
        },
        propEntregas: {
            required: true,
        },
        propEntregaSelecionada: {
            default: null,
        },
        destaqueEntregaId: {
            type: [Number, String],
            default: null,
        },
        isAuditor: {
            type: Boolean,
            default: false,
        },
        isGerente: {
            type: Boolean,
            default: false,
        },
        idUsuario: {
            required: true,
        },
        somenteLeitura: {
            type: Boolean,
            default: false,
        },
        carregando: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            coordenadores: [],
            showModal: false,
            entregaSelecionada: this.propEntregaSelecionada ?? null,
            entregas: this.propEntregas,
            modalResultado: false,
            entregaEditada: {},
            statusEntregaAntesConclusao: null,
            ordemEntregasAntesDrag: null,
            resultadoSelecionadoId: null,
            destaqueEntregaAplicado: false,
            reunioesPacote: [],
            carregandoReunioesPacote: false,
        }
    },
    computed: {
        resultados() {
            return Array.isArray(this.propPcm?.resultados) ? this.propPcm.resultados : []
        },
        entregasExibidas: {
            get() {
                const resultadoId = this.resultadoSelecionadoId
                if (resultadoId == null) return this.entregas
                return this.entregas.filter((e) =>
                    this.entregaSemResultado(e) || e.resultado_id == resultadoId,
                )
            },
            set(novaOrdemFiltrada) {
                const resultadoId = this.resultadoSelecionadoId
                if (resultadoId == null) {
                    this.entregas = novaOrdemFiltrada
                    return
                }
                const idsFiltrados = new Set(novaOrdemFiltrada.map((e) => e.id))
                const indices = []
                this.entregas.forEach((e, i) => {
                    if (idsFiltrados.has(e.id)) indices.push(i)
                })
                const novaLista = [...this.entregas]
                novaOrdemFiltrada.forEach((e, i) => {
                    novaLista[indices[i]] = e
                })
                this.entregas = novaLista
            },
        },
        podeEditar() {
            return !this.somenteLeitura
        },
        entregaSelecionadaConcluida() {
            return entregaConcluida(this.entregaSelecionada)
        },
        podeEditarEntregaSelecionada() {
            return this.podeEditar && !this.entregaSelecionadaConcluida
        },
        podeReordenarEntregas() {
            return this.podeEditar && (this.isGerente || this.isAuditor)
        },
        temObservacaoRepactuacao() {
            return Boolean(String(this.entregaSelecionada?.observacao_repactuacao || '').trim())
        },
    },
    watch: {
        /** Só quando o pai troca a referência do array (ex.: getEntregas). Sem `deep` para não
         * reagir a reorder/toggles locais e sobrescrever a ordem arrastada. */
        propEntregas(v) {
            if (Array.isArray(v)) {
                this.entregas = v.map((e) => this.normalizarDatasEntrega({ ...e }))
                this.sanitizarVinculosResultadoOrfaos()
                this.aplicarDestaqueEntregaSeNecessario()
                this.sincronizarEntregaSelecionada()
            }
        },
        propEntregaSelecionada(v) {
            const idProp = v?.id ?? null
            const idLocal = this.entregaSelecionada?.id ?? null
            if (idProp !== idLocal) {
                this.entregaSelecionada = v ? this.normalizarDatasEntrega({ ...v }) : null
            }
        },
        'entregaSelecionada.id': {
            immediate: true,
            handler(id) {
                this.carregarReunioesPacote(id)
            },
        },
        resultados: {
            immediate: true,
            handler(resultados) {
                if (!resultados.length) {
                    this.resultadoSelecionadoId = null
                    this.sanitizarVinculosResultadoOrfaos()
                    this.sincronizarEntregaSelecionada()
                    return
                }
                if (!this.destaqueEntregaAplicado && this.destaqueEntregaId) {
                    const preferido = this.resultadoIdDoDestaque()
                    if (preferido != null && resultados.some((r) => r.id == preferido)) {
                        this.resultadoSelecionadoId = preferido
                    } else {
                        const aindaExiste = resultados.some((r) => r.id == this.resultadoSelecionadoId)
                        if (this.resultadoSelecionadoId == null || !aindaExiste) {
                            this.resultadoSelecionadoId = resultados[0].id
                        }
                    }
                } else {
                    const aindaExiste = resultados.some((r) => r.id == this.resultadoSelecionadoId)
                    if (this.resultadoSelecionadoId == null || !aindaExiste) {
                        this.resultadoSelecionadoId = resultados[0].id
                    }
                }
                this.sanitizarVinculosResultadoOrfaos()
                this.aplicarDestaqueEntregaSeNecessario()
                this.sincronizarEntregaSelecionada()
            },
        },
    },
    async created() {
        if (Array.isArray(this.entregas)) {
            this.entregas = this.entregas.map((e) => this.normalizarDatasEntrega({ ...e }))
        }
        if (this.entregaSelecionada) {
            this.entregaSelecionada = this.normalizarDatasEntrega({ ...this.entregaSelecionada })
        }
        this.coordenadores = await serviceProjetos.getUsuarios();
    },
    methods: {
        dataParaInput,
        formatarDataBr,
        formatarDataHoraBr,
        fimPrevistoOriginal,
        exibirFimPrevistoOriginal,
        tituloFimPrevistoOriginal,
        formatarCustoEntrega(entrega) {
            const n = Number(entrega?.custo_total)
            const valor = Number.isFinite(n) ? n : 0
            return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor)
        },
        onEntregasDragStart() {
            if (!this.podeReordenarEntregas) return
            this.ordemEntregasAntesDrag = this.entregas.map((e) => e.id)
        },
        podeMoverEntregaNoDrag(evt) {
            const idx = evt?.draggedContext?.index
            if (idx == null) return true
            const entrega = this.entregasExibidas[idx]
            return !entregaConcluida(entrega)
        },
        selecionarResultado(id) {
            this.resultadoSelecionadoId = id
            this.sincronizarEntregaSelecionada()
        },
        resultadoIdDoDestaque() {
            if (!this.destaqueEntregaId) return null
            const id = Number(this.destaqueEntregaId)
            const entrega = this.entregas.find((e) => e.id === id)
            if (!entrega || !this.resultadoIdValido(entrega.resultado_id)) return null
            return entrega.resultado_id
        },
        aplicarDestaqueEntregaSeNecessario() {
            if (this.destaqueEntregaAplicado || !this.destaqueEntregaId || !this.entregas.length) return
            const id = Number(this.destaqueEntregaId)
            const entrega = this.entregas.find((e) => e.id === id)
            if (!entrega) return
            if (this.resultadoIdValido(entrega.resultado_id)) {
                this.resultadoSelecionadoId = entrega.resultado_id
            }
            if (this.entregaSelecionada?.id !== entrega.id) {
                this.entregaSelecionada = entrega
                this.$emit('mudarEntrega', entrega)
            }
            this.destaqueEntregaAplicado = true
        },
        sincronizarEntregaSelecionada() {
            const idAtual = this.entregaSelecionada?.id ?? null
            if (idAtual != null && !this.entregas.length) return

            const visiveis = this.entregasExibidas
            const aindaVisivel = idAtual != null && visiveis.some((e) => e.id == idAtual)

            if (aindaVisivel) return

            const proxima = visiveis[0] ?? null
            const idProxima = proxima?.id ?? null

            if (idProxima === idAtual) return

            this.entregaSelecionada = proxima
            this.$emit('mudarEntrega', proxima)
        },
        rotuloResultado,
        tituloResultado,
        entregaSemResultado(entrega) {
            return !this.resultadoIdValido(entrega?.resultado_id)
        },
        entregaIncompleta(entrega) {
            if (!entrega) return true

            const nome = String(entrega.nome ?? '').trim()
            if (!nome) return true

            const coordenadorId = entrega.coordenador_id
            if (coordenadorId == null || coordenadorId === '') return true

            if (!this.resultadoIdValido(entrega.resultado_id)) return true

            const dtInicio = entrega.dtInicio
            const dtFim = entrega.dtFim
            if (!dtInicio || dtInicio === '') return true
            if (!dtFim || dtFim === '') return true

            return false
        },
        resultadoIdValido(id) {
            if (id == null || id === '') return false
            return this.resultados.some((r) => r.id == id)
        },
        sanitizarVinculosResultadoOrfaos() {
            const limpar = (entrega) => {
                if (!entrega || this.resultadoIdValido(entrega.resultado_id)) return
                entrega.resultado_id = null
            }
            this.entregas.forEach(limpar)
            limpar(this.entregaSelecionada)
            limpar(this.entregaEditada)
        },
        async onEntregasDragEnd(evt) {
            if (!this.podeReordenarEntregas || evt.oldIndex === evt.newIndex) return
            try {
                await serviceProjetos.reordenarEntregas({
                    projeto_id: this.idProjeto,
                    ordem: this.entregas.map((e) => e.id),
                })
                this.entregas.forEach((e, i) => {
                    e.index = i
                })
                this.$emit('entregas-reordenadas')
            } catch (err) {
                const ordemAntes = this.ordemEntregasAntesDrag
                if (ordemAntes?.length) {
                    const byId = Object.fromEntries(this.entregas.map((x) => [x.id, x]))
                    this.entregas.splice(
                        0,
                        this.entregas.length,
                        ...ordemAntes.map((id) => byId[id]).filter(Boolean),
                    )
                }
                if (process.env.NODE_ENV !== 'production') console.error(err)
            }
        },
        isCoordenador(id) {
            return (this.idUsuario == id) ?? false
        },
        nomeCoordenador(id) {
            const usuario = (this.coordenadores || []).find((u) => u.id == id)
            return usuario?.usuario_nome || 'Não definido'
        },
        entregaEstaConcluida(entrega) {
            return entregaConcluida(entrega)
        },
        textoPrazo(entrega) {
            if (entregaStatusEhConcluido(entrega)) return 'Concluído'
            if (entregaStatusEhCancelado(entrega)) return 'Cancelada'
            const dtFim = entrega?.dtFim
            if (!dtFim) return 'Sem prazo definido'
            const dias = this.diasParaPrazo(dtFim)
            if (dias === 0) return 'Termina hoje'
            if (dias > 0) return `${dias} dia(s) restantes`
            return `${Math.abs(dias)} dia(s) em atraso`
        },
        /** Evita new Date('YYYY-MM-DD'), que é UTC e desloca o dia no fuso local. */
        dataLocalAPartirDeString(dt) {
            if (!dt) return null
            const s = String(dt).slice(0, 10)
            const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
            if (!m) return null
            const y = Number(m[1])
            const mo = Number(m[2])
            const d = Number(m[3])
            const local = new Date(y, mo - 1, d)
            return Number.isNaN(local.getTime()) ? null : local
        },
        diasParaPrazo(dtFim) {
            if (!dtFim) return null

            const hoje = new Date()
            hoje.setHours(0, 0, 0, 0)
            const fim = this.dataLocalAPartirDeString(dtFim)
            if (!fim) return null
            fim.setHours(0, 0, 0, 0)
            const diffMs = fim - hoje
            return Math.floor(diffMs / 86400000)
        },
        classePrazo(entrega) {
            if (entregaStatusEhConcluido(entrega)) return 'bg-sucesso'
            if (entregaStatusEhCancelado(entrega)) return 'bg-cinza'
            const dias = this.diasParaPrazo(entrega?.dtFim)
            if (dias === null) return 'bg-cinza'
            if (dias < 0) return 'bg-erro'
            if (dias === 0) return 'bg-alerta'
            if (dias <= 7) return 'bg-ok'
            return 'bg-ok'
        },
        /** Garante YYYY-MM-DD para input type="date" (API pode devolver ISO com hora). */
        normalizarDatasEntrega(patch) {
            if (!patch || typeof patch !== 'object') return patch
            const o = { ...patch }
            if ('dtInicio' in o && o.dtInicio != null && o.dtInicio !== '') {
                o.dtInicio = dataParaInput(o.dtInicio)
            }
            if ('dtFim' in o && o.dtFim != null && o.dtFim !== '') {
                o.dtFim = dataParaInput(o.dtFim)
            }
            if ('dt_conclusao' in o && o.dt_conclusao != null && o.dt_conclusao !== '') {
                o.dt_conclusao = dataParaInput(o.dt_conclusao)
            }
            if ('index' in o && o.index != null) {
                o.index = Number(o.index)
            }
            return o
        },
        aplicarEntregaAtualizadaNoEstado(atualizado) {
            if (!atualizado?.id) return
            const patch = this.normalizarDatasEntrega(atualizado)
            const idx = this.entregas.findIndex((e) => e.id == patch.id)
            if (idx !== -1) {
                this.entregas.splice(idx, 1, { ...this.entregas[idx], ...patch })
            }
            if (this.entregaSelecionada?.id == patch.id) {
                Object.assign(this.entregaSelecionada, patch)
            }
            if (this.entregaEditada?.id == patch.id) {
                Object.assign(this.entregaEditada, patch)
            }
            if ('resultado_id' in patch) {
                this.sincronizarEntregaSelecionada()
            }
        },
        fecharModalResultado() {
            this.modalResultado = false
            if (this.entregaEditada?.id && this.statusEntregaAntesConclusao != null) {
                this.reverterStatusEntrega(this.entregaEditada.id, this.statusEntregaAntesConclusao)
            }
        },

        async concluirEntrega(payload) {
            if (this.somenteLeitura) return
            const id = this.entregaEditada?.id
            if (!id || !payload?.resultado_alcancado?.trim()) return

            const impedem = await this.obterTarefasImpedindoConclusao(id)
            if (impedem.length) {
                window.alert(mensagemTarefasImpedemConclusaoEntrega(impedem))
                this.reverterStatusEntrega(id, this.statusEntregaAntesConclusao)
                this.modalResultado = false
                return
            }

            try {
                const atualizado = await serviceProjetos.atualizarEntrega({
                    entrega_id: id,
                    resultado_alcancado: payload.resultado_alcancado.trim(),
                    status: 'Concluído',
                })
                this.aplicarEntregaAtualizadaNoEstado(atualizado)
                this.modalResultado = false
                this.$emit('entrega-atualizada', atualizado)
            } catch (e) {
                if (process.env.NODE_ENV !== 'production') console.error(e)
            }
        },

        mudarEntrega(entrega) {
            this.entregaSelecionada = entrega;
            this.$emit('mudarEntrega', entrega);
        },

        async carregarReunioesPacote(id) {
            if (!id) {
                this.reunioesPacote = []
                return
            }
            this.carregandoReunioesPacote = true
            try {
                this.reunioesPacote = await serviceProjetos.listarReunioesDaEntrega(id)
            } catch (_) {
                this.reunioesPacote = []
            } finally {
                this.carregandoReunioesPacote = false
            }
        },

        async editarStatus(id, payload) {
            if (this.somenteLeitura) return
            const entrega = this.entregas.find((e) => e.id == id)
            if (entregaConcluida(entrega)) return
            this.entregaEditada = this.entregas.find(e => e.id == id)
            if (payload.status === 'Concluído') {
                const impedem = await this.obterTarefasImpedindoConclusao(id)
                if (impedem.length) {
                    window.alert(mensagemTarefasImpedemConclusaoEntrega(impedem))
                    this.reverterStatusEntrega(id, payload.statusAnterior)
                    return
                }
                this.statusEntregaAntesConclusao = payload.statusAnterior ?? null
                this.modalResultado = true
            }
            else {
                this.atualizarEntrega('status', payload.status, id);
            }
        },

        async obterTarefasImpedindoConclusao(entregaId) {
            try {
                const response = await serviceProjetos.getSprints(entregaId)
                const tarefas = extrairTarefasDeSprints(response?.sprints)
                return tarefasImpedemConclusaoEntrega(tarefas)
            } catch {
                return []
            }
        },

        reverterStatusEntrega(id, statusAnterior) {
            if (!statusAnterior) return

            const aplicar = (entrega) => {
                if (entrega?.id == id) entrega.status = statusAnterior
            }
            this.entregas.forEach(aplicar)
            aplicar(this.entregaSelecionada)
            aplicar(this.entregaEditada)
        },

        async atualizarEntrega(itemEditado, valor, id) {
            if (this.somenteLeitura) return
            const entrega = this.entregas.find((e) => e.id == id)
            if (entregaConcluida(entrega) && !(this.isAuditor && itemEditado === 'dt_conclusao')) return
            var payload = {
                entrega_id: id,
                [itemEditado]: valor,
            }
            const atualizado = await serviceProjetos.atualizarEntrega(payload)
            this.aplicarEntregaAtualizadaNoEstado(atualizado)
        },
        async excluirEntrega(id) {
            if (this.somenteLeitura) return
            const entrega = this.entregas.find((e) => e.id == id)
            if (entregaConcluida(entrega)) return
            if (confirm('Tem certeza de que deseja excluir o pacote de trabalho?')) {
                var payload = {
                    entrega_id: id,
                    usuario_id: this.idUsuario
                }
                await serviceProjetos.excluirEntrega(payload)
                this.entregas = this.entregas.filter(e => e.id != id)
                if (this.entregaSelecionada?.id == id) {
                    this.sincronizarEntregaSelecionada()
                }
            }
        },
        async gravarNovaEntrega(entrega) {
            if (this.somenteLeitura) return
            entrega.projeto_id = this.idProjeto;

            var response = await serviceProjetos.gravarNovaEntrega(entrega);
            response.status = 'Pendente';
            if (response.index == null && response.indice != null) {
                response.index = response.indice
            }
            const nova = this.normalizarDatasEntrega(response)
            this.entregas.push(nova)
            this.mudarEntrega(nova)
            this.showModal = false
        },
    },
}
</script>

<style scoped>
.entregas-lista-bloco {
    margin-bottom: 1rem;
    width: 100%;
}

/* Grid no mesmo elemento que o Sortable usa como container (filhos = células) */
.cards-entregas-drag-host {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: .75rem;
    align-items: stretch;
    width: 100%;
    min-width: 0;
}

/*
 * animation=0: a animação interna do Sortable em grid empilha células e sobrepõe texto.
 * Sem forceFallback: evita clone no body + original na grade (efeito «dois cards» no mesmo slot).
 */
.cards-entregas-drag-host :deep(.card-entrega--sortable-chosen) {
    transition: none !important;
    transform: none !important;
    box-shadow: none;
}

.cards-entregas-drag-host :deep(.card-entrega--sortable-ghost) {
    transition: none !important;
    transform: none !important;
    opacity: 0.45;
    background: color-mix(in srgb, var(--cor-bg) 88%, var(--cor-separador));
    border-style: dashed;
    border-color: color-mix(in srgb, var(--cor-primaria) 45%, var(--cor-separador));
    box-shadow: none;
}

.cards-entregas-drag-host:has(.card-entrega--sortable-chosen) .card-entrega:hover {
    transform: none;
    box-shadow: none;
}

.card-entrega {
    border: 1px solid var(--cor-separador);
    border-radius: 8px;
    padding: .75rem;
    cursor: pointer;
    background: var(--cor-bg);
    transition: .15s ease;
    display: flex;
    flex-direction: column;
    min-height: 122px;
    height: 100%;
    box-sizing: border-box;
}

.card-entrega--arrastavel {
    cursor: grab;
}

.card-entrega--arrastavel:active {
    cursor: grabbing;
}

.card-entrega:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 14px rgba(0, 0, 0, .06);
}

.card-entrega.selecionado {
    background-color: var(--cor-primaria-fraca);
    border-color: var(--cor-primaria);
    box-shadow: 0 0 0 1px color-mix(in srgb, var(--cor-primaria) 25%, transparent);
}

.card-entrega--incompleta {
    border-color: var(--cor-erro);
    background: color-mix(in srgb, var(--cor-erro) 10%, var(--cor-bg));
    box-shadow: 0 0 0 1px color-mix(in srgb, var(--cor-erro) 30%, transparent);
}

.card-entrega--incompleta.selecionado {
    background: color-mix(in srgb, var(--cor-erro) 14%, var(--cor-primaria-fraca));
    border-color: var(--cor-erro);
    box-shadow: 0 0 0 1px color-mix(in srgb, var(--cor-erro) 40%, transparent);
}

.card-entrega__nome {
    font-size: 14px;
    font-weight: 700;
    margin-bottom: .45rem;
    line-height: 1.25rem;
    min-height: 2.5rem;
    line-clamp: 2;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.card-entrega__meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: .5rem;
    margin-top: .35rem;
}

.card-entrega__meta--prazo {
    margin-top: auto;
    padding-top: .45rem;
}

.card-entrega__label {
    font-size: 12px;
    color: var(--cor-fonte-fraca);
}

.card-entrega__valor {
    font-size: 13px;
    text-align: right;
}

.form-entrega {
    margin-top: 1rem;
    border: 1px solid var(--cor-separador);
    border-radius: 10px;
    padding: 1rem;
    background: color-mix(in srgb, var(--cor-bg) 92%, white);
}

.cabecalho-form-entrega {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: .5rem;
}

.cabecalho-form-entrega h3 {
    margin: 0;
}

.bloco-form {
    margin-top: .75rem;
    padding-top: .75rem;
    border-top: 1px solid var(--cor-separador);
}

.tarefas span {
    font-size: 14px;
}

.tarefas span span {
    font-weight: bold;
}

.btn-excluir-entrega {
    border: 1px solid var(--cor-erro);
    color: var(--cor-erro);
    background: var(--cor-bg);
    border-radius: 6px;
    padding: .45rem .7rem;
    display: inline-flex;
    gap: .4rem;
    align-items: center;
    cursor: pointer;
    font-size: 14px;
}

.btn-excluir-entrega:hover {
    background: color-mix(in srgb, var(--cor-erro) 12%, transparent);
}

.card-entrega--add {
    border-style: dashed;
    border-color: var(--cor-primaria);
    color: var(--cor-primaria);
    min-height: 122px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: .35rem;
    font-weight: 600;
    font-size: 13px;
    padding: .65rem .5rem;
    height: 100%;
}

.card-entrega--add i {
    font-size: 20px;
}

.card-entrega--add:hover {
    background: color-mix(in srgb, var(--cor-primaria) 8%, transparent);
    box-shadow: 0 6px 14px rgba(0, 0, 0, .04);
}

@media (max-width: 980px) {
    .cabecalho-form-entrega {
        flex-direction: column;
        align-items: flex-start;
        gap: .5rem;
    }
}

.resultado {
    text-align: left;
    max-width: 16rem;
    text-wrap: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
}

/* Chip ao lado do campo: a linha não cresce e o input fica igual aos demais. */
.fim-previsto {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
}

.fim-previsto input[type="date"] {
    flex: 1;
    min-width: 0;
}

.lista-reunioes-pacote {
    list-style: none;
    margin: 0.4rem 0 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
}

.lista-reunioes-pacote li {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
}

.fim-previsto-original {
    flex-shrink: 0;
    margin: 0;
    line-height: 20px;
    white-space: nowrap;
}
</style>